import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { AppIcon } from "../../components/ui/AppIcon";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { PageHeader } from "../../components/ui/PageHeader";
import { SearchInput } from "../../components/ui/SearchInput";
import { Select } from "../../components/ui/Select";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { type Column, Table } from "../../components/ui/Table";
import { ApiError } from "../../lib/api";
import { type Role, type UserRow, usersApi } from "../../lib/api/users";
import { formatDateTime } from "../../lib/format";
import { t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

const ROLES: Role[] = ["superadmin", "platform_admin", "member"];

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

function roleTone(role: Role): "info" | "warning" | "neutral" {
  if (role === "superadmin") return "warning";
  if (role === "platform_admin") return "info";
  return "neutral";
}

export function UsersListPage() {
  useLocale();
  const navigate = useNavigate();
  const [role, setRole] = useState<Role | "">("");
  const [active, setActive] = useState<"" | "true" | "false">("");
  const [search, setSearch] = useState("");

  const list = useQuery({
    queryKey: ["users", role, active, search],
    queryFn: () =>
      usersApi.list({
        role: (role || undefined) as Role | undefined,
        is_active: active === "" ? undefined : active === "true",
        search: search || undefined,
      }),
  });

  const columns: Column<UserRow>[] = [
    {
      key: "name",
      header: t("users.list.column.fullName"),
      render: (row) => (
        <div className="app-cell">
          <AppIcon name={row.full_name || row.email} packageId={row.id} size={32} />
          <div>
            <Link to={`/users/${row.id}`} className="app-name">
              {row.full_name || row.email}
            </Link>
            <div className="app-pkg">{row.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "role",
      header: t("users.list.column.role"),
      render: (row) => <Badge tone={roleTone(row.role)}>{t(`users.roles.${row.role}`)}</Badge>,
    },
    {
      key: "code",
      header: t("users.list.column.employeeCode"),
      render: (row) => row.employee_code || "—",
    },
    {
      key: "status",
      header: t("common.status"),
      render: (row) =>
        row.is_active ? (
          <Badge tone="success">{t("common.active")}</Badge>
        ) : (
          <Badge tone="danger">{t("common.inactive")}</Badge>
        ),
    },
    {
      key: "devices",
      header: t("users.list.column.devices"),
      render: (row) => row.devices_count,
      align: "right",
    },
    {
      key: "last_login",
      header: t("users.list.column.lastLogin"),
      render: (row) => formatDateTime(row.last_login),
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <Link to={`/users/${row.id}`}>
          <Button variant="secondary" size="sm">
            {t("common.view")}
          </Button>
        </Link>
      ),
      align: "right",
    },
  ];

  return (
    <div>
      <PageHeader
        title={t("users.list.title")}
        subtitle={t("users.list.subtitle")}
        actions={
          <Button onClick={() => navigate("/users/new")}>{t("users.list.action.create")}</Button>
        }
      />

      <div className="filter-bar">
        <SearchInput
          placeholder={t("users.list.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={role} onChange={(e) => setRole(e.target.value as Role | "")} aria-label={t("users.list.column.role")}>
          <option value="">{t("users.list.column.role")}</option>
          {ROLES.map((r) => (
            <option value={r} key={r}>
              {t(`users.roles.${r}`)}
            </option>
          ))}
        </Select>
        <Select value={active} onChange={(e) => setActive(e.target.value as typeof active)} aria-label={t("common.status")}>
          <option value="">{t("common.status")}</option>
          <option value="true">{t("common.active")}</option>
          <option value="false">{t("common.inactive")}</option>
        </Select>
      </div>

      <div className="stack">
        <Card padding="none">
          {list.isLoading && <LoadingState />}
          {list.error && (
            <ErrorState
              titleKey="states.errorTitle"
              message={translateError(list.error)}
              action={
                <Button variant="secondary" size="sm" onClick={() => void list.refetch()}>
                  {t("common.retry")}
                </Button>
              }
            />
          )}
          {list.data && (
            <Table
              rows={list.data}
              columns={columns}
              rowKey={(row) => row.id}
              empty={
                <EmptyState
                  titleKey="users.list.emptyTitle"
                  bodyKey="users.list.emptyBody"
                  action={
                    <Button onClick={() => navigate("/users/new")}>
                      {t("users.list.action.create")}
                    </Button>
                  }
                />
              }
            />
          )}
        </Card>
      </div>
    </div>
  );
}
